import type { PublicGame } from '~/domains/contest/domains/services/interfaces/propsService/api/game';

import type {
  Team,
  MarketPrice,
  SpreadMarketData,
  TotalMarketData,
  MoneylineMarketData,
} from '../../components/MarketCard/types';
import { useEntitiesStore } from '../entities.store';
import { useNormalizedMarketsStore } from '../normalizedMarkets.store';
import {
  getMarketAndSideForSelection,
  isLockedByLowLiquidity,
  type MarketType as LiquidityMarketType,
  type TeamType as LiquidityTeamType,
} from '../../utils/marketLock';

type TeamType = LiquidityTeamType;

function arePricesEqual(a: MarketPrice | undefined, b: MarketPrice | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.price === b.price && a.american === b.american;
}

function areSpreadEqual(a: SpreadMarketData | undefined, b: SpreadMarketData | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.marketId === b.marketId && a.lineDisplay === b.lineDisplay && arePricesEqual(a.price, b.price)
  );
}

function areTotalEqual(a: TotalMarketData | undefined, b: TotalMarketData | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.marketId === b.marketId && a.lineDisplay === b.lineDisplay && arePricesEqual(a.price, b.price)
  );
}

function areMoneylineEqual(
  a: MoneylineMarketData | undefined,
  b: MoneylineMarketData | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.marketId === b.marketId && arePricesEqual(a.price, b.price);
}

export const useMarketEventStart = (marketGroupId: string): number | undefined =>
  useNormalizedMarketsStore((state) => {
    const marketGroup = state.normalizedMarketGroupsById.get(marketGroupId);
    return marketGroup?.eventStart;
  });

const useMarketGroupEventId = (marketGroupId: string): string | undefined =>
  useNormalizedMarketsStore((state) => {
    const marketGroup = state.normalizedMarketGroupsById.get(marketGroupId);
    return marketGroup?.eventId;
  });

export const useMarketEvent = (marketGroupId: string): PublicGame | undefined => {
  const eventId = useMarketGroupEventId(marketGroupId);

  return useEntitiesStore((state) => (eventId ? state.eventsById.get(eventId) : undefined));
};

export const useMarketMatchupLabel = (marketGroupId: string): string => {
  const eventId = useMarketGroupEventId(marketGroupId);

  return useEntitiesStore((state) => {
    if (!eventId) return '';

    const awayTeam = state.normalizedTeamsByEventAndType.get(`${eventId}:away`);
    const homeTeam = state.normalizedTeamsByEventAndType.get(`${eventId}:home`);

    if (!awayTeam || !homeTeam) return '';

    return `${awayTeam.name} at ${homeTeam.name}`;
  });
};

export const useMarketTeam = (marketGroupId: string, teamType: TeamType): Team | undefined => {
  const eventId = useMarketGroupEventId(marketGroupId);

  return useEntitiesStore((state) => {
    if (!eventId) return undefined;
    return state.normalizedTeamsByEventAndType.get(`${eventId}:${teamType}`);
  });
};

export const useMarketSpread = (
  marketGroupId: string,
  teamType: TeamType
): SpreadMarketData | undefined =>
  useNormalizedMarketsStore((state) => {
    const marketGroup = state.normalizedMarketGroupsById.get(marketGroupId);
    if (!marketGroup) return undefined;

    return teamType === 'home' ? marketGroup.homeTeamLine.spread : marketGroup.awayTeamLine.spread;
  }, areSpreadEqual);

export const useMarketTotal = (
  marketGroupId: string,
  teamType: TeamType
): TotalMarketData | undefined =>
  useNormalizedMarketsStore((state) => {
    const marketGroup = state.normalizedMarketGroupsById.get(marketGroupId);
    if (!marketGroup) return undefined;

    return teamType === 'home' ? marketGroup.homeTeamLine.total : marketGroup.awayTeamLine.total;
  }, areTotalEqual);

export const useMarketMoneyline = (
  marketGroupId: string,
  teamType: TeamType
): MoneylineMarketData | undefined =>
  useNormalizedMarketsStore((state) => {
    const marketGroup = state.normalizedMarketGroupsById.get(marketGroupId);
    if (!marketGroup) return undefined;

    return teamType === 'home'
      ? marketGroup.homeTeamLine.moneyline
      : marketGroup.awayTeamLine.moneyline;
  }, areMoneylineEqual);

export function useIsMarketLockedByLiquidity(
  marketGroupId: string,
  marketType: LiquidityMarketType,
  teamType: TeamType
): boolean {
  const team = useMarketTeam(marketGroupId, teamType);

  return useNormalizedMarketsStore((state) => {
    if (!team) return false;

    const marketGroup = state.normalizedMarketGroupsById.get(marketGroupId);
    if (!marketGroup) return false;

    const { market, side } = getMarketAndSideForSelection({
      markets: marketGroup.markets,
      marketType,
      teamId: team.id,
      teamType,
    });

    if (!market) return false;

    return isLockedByLowLiquidity({ market, side });
  });
}
